import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useMsal } from "@azure/msal-react";
import apiClient from "../../apiClient";
import logo from "../logo.png";
import NavDropdown from "./NavDropdown";
import NavItem from "./NavItem";
import MobileMenu from "./MobileMenu";
import '../../styles/navigation/Header.css';

function Header() {
  const location = useLocation();
  const navigate = useNavigate();
  const { instance } = useMsal();
  const headerRef = useRef(null);
  const isLoginPage = location.pathname === "/";

  // Check if user is authenticated
  const isAuthenticated = () => {
    const token = localStorage.getItem("authToken");
    const role = localStorage.getItem("role") || localStorage.getItem("userRole");
    return !!(token && role);
  };

  const [permissions, setPermissions] = useState({
    canAccessHR: false,
    canAccessTA: false,
    canAccessLD: false,
    canAccessFLM: false,
    canAccessFLMTrackers: false,
  });

  const [activeDropdown, setActiveDropdown] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userEmail, setUserEmail] = useState(null);

  // Check if user can see environment switch button
  const canSeeEnvSwitch = () => {
    const allowedEmails = [
      "lbarbosa@cafuamanagement.com",
      "lmarques@cafuamanagement.com"
    ];
    return allowedEmails.includes(userEmail?.toLowerCase());
  };

  // Determine environment switch URL
  const getEnvSwitchUrl = () => {
    const hostname = window.location.hostname;
    // If we're on staging, link to production. Otherwise, link to staging.
    if (hostname.includes('staging')) {
      return 'https://peopleplan.cafuamanagement.com';
    }
    return 'https://staging.cafuamanagement.com';
  };

  // Get environment switch button label
  const getEnvSwitchLabel = () => {
    const hostname = window.location.hostname;
    if (hostname.includes('staging')) {
      return 'Switch to Production';
    }
    return 'Switch to Staging';
  };

  // Navigation structure configuration
  const navigationConfig = [
    {
      id: 'dashboard',
      label: 'Dashboard',
      path: '/dashboard',
      type: 'link',
    },
    {
      id: 'peopleplan',
      label: 'People Plan',
      path: '/peopleplan',
      type: 'link',
    },
    {
      id: 'hr',
      label: 'HR',
      path: '/hr',
      type: 'dropdown',
      requiresPermission: 'canAccessHR',
      items: [
        { label: 'HR Dashboard', path: '/hr' },
        { label: 'HR Requests', path: '/hr/crm/tickets' },
        { label: 'To Do Tickets', path: '/hr/crm/todo-tickets' },
      ]
    },
    {
      id: 'ta',
      label: 'TA',
      path: '/ta',
      type: 'dropdown',
      requiresPermission: 'canAccessTA',
      items: [
        { label: 'TA Dashboard', path: '/ta' },
        { label: 'Executive Dashboard', path: '/recruiting/executive' },
        { label: 'Pipeline Dashboard', path: '/recruiting/pipeline' },
        { label: 'Hires Dashboard', path: '/recruiting/hires' },
        { label: 'TA Requests', path: '/ta/crm/tickets' },
        { label: 'To Do Tickets', path: '/ta/requests' },
        { label: 'Job Tracker', path: '/ta/jobs' },
        { label: 'Candidate Tracker', path: '/ta/candidates' },
        { label: 'Onboarding', path: '/ta/newhires' },
      ]
    },
    {
      id: 'ld',
      label: 'L&D',
      path: '/ld',
      type: 'dropdown',
      requiresPermission: 'canAccessLD',
      items: [
        { label: 'L&D Dashboard', path: '/ld' },
        { label: 'Weekly Checkins', path: '/ld/checkins', requiresPermission: 'canAccessFLMTrackers' },
        { label: 'Depository Files', path: '/ld/files', requiresPermission: 'canAccessFLM' },
        { label: 'Window Readiness Results', path: '/ld/window-readiness', requiresPermission: 'canAccessFLM' },
        // Hidden for now - will be enabled in the future
        // { label: 'Goals & OKRs', path: '/peoplepulse/goals' },
        // { label: '1-on-1 Meetings', path: '/peoplepulse/one-on-one' },
        // { label: 'Performance Reviews', path: '/peoplepulse/reviews' },
        // { label: 'Feedback & Recognition', path: '/peoplepulse/feedback' },
        // { label: 'Tasks & Action Items', path: '/peoplepulse/tasks' },
      ]
    },
    {
      id: 'settings',
      label: 'Settings',
      path: '/settings',
      type: 'link',
    },
    {
      id: 'it',
      label: 'IT',
      type: 'external',
      url: 'http://support.cafuamanagement.com/helpdesk/WebObjects/Helpdesk.woa',
      target: '_blank',
    },
  ];

  // Check permissions
  useEffect(() => {
    const checkPermissions = async () => {
      if (isLoginPage) return;

      try {
        const token = localStorage.getItem("authToken");
        const userRole = localStorage.getItem("userRole");
        const storedEmail = localStorage.getItem("userEmail");

        if (!token) {
          setPermissions({
            canAccessHR: false,
            canAccessTA: false,
            canAccessLD: false,
            canAccessFLM: false,
            canAccessFLMTrackers: false,
          });
          return;
        }

        // Set user email for environment switch button
        setUserEmail(storedEmail);

        // Check FLM Upload access using apiClient
        let canAccessFLM = false;
        try {
          const response = await apiClient.get('/api/flm/upload-history');
          canAccessFLM = response.status === 200;
        } catch (err) {
          // API call failed - user doesn't have FLM access
          canAccessFLM = false;
        }

        // FLM Trackers access
        const allowedEmails = [
          "lbarbosa@cafuamanagement.com",
          "adiaz@cafuamanagement.com",
          "cbrown@cafuamanagement.com",
          "cleathers@cafuamanagement.com",
          "jwolkens@cafuamanagement.com",
          "nlarochelle@cafuamanagement.com",
          "smoreschi@cafuamanagement.com",
          "kkeys@cafuamanagement.com",
          "lmarques@cafuamanagement.com",
          "fgcafua@cafuamanagement.com",
          "rgonzalez@cafuamanagement.com",
          "jfrancisco@cafuamanagement.com",
        ];

        // Normalize role to lowercase for comparison
        const normalizedRole = userRole?.toLowerCase();

        const ldRoles = ["ld", "ld_director", "admin"];
        const hasRoleAccess = ldRoles.includes(normalizedRole);
        const hasEmailAccess = allowedEmails.includes(userEmail?.toLowerCase());
        const canAccessFLMTrackers = hasRoleAccess || hasEmailAccess;

        // Department access based on roles (using lowercase role names)
        const hrRoles = ["hr", "admin"];
        const taRoles = ["ta", "admin"];
        const ldRolesAccess = ["ld", "ld_director", "admin"];

        setPermissions({
          canAccessHR: hrRoles.includes(normalizedRole),
          canAccessTA: taRoles.includes(normalizedRole),
          canAccessLD: ldRolesAccess.includes(normalizedRole) || canAccessFLM || canAccessFLMTrackers,
          canAccessFLM,
          canAccessFLMTrackers,
        });

      } catch (error) {
        console.error("Error checking permissions:", error);
        setPermissions({
          canAccessHR: false,
          canAccessTA: false,
          canAccessLD: false,
          canAccessFLM: false,
          canAccessFLMTrackers: false,
        });
      }
    };

    checkPermissions();
  }, [isLoginPage, location.pathname]);

  // Handle logout
  const handleLogout = async () => {
    // Clear all local storage items
    localStorage.removeItem("authToken");
    localStorage.removeItem("userRole");
    localStorage.removeItem("role");
    localStorage.removeItem("userEmail");
    localStorage.removeItem("userId");
    localStorage.removeItem("userName");
    localStorage.removeItem("lastActivity");

    // Sign out from MSAL to prevent auto-login on return to login page
    try {
      await instance.logoutPopup({
        postLogoutRedirectUri: window.location.origin,
        mainWindowRedirectUri: "/"
      });
    } catch (error) {
      // If popup fails, try redirect logout
      console.error("Popup logout failed, trying redirect:", error);
      await instance.logoutRedirect({
        postLogoutRedirectUri: window.location.origin
      });
    }
  };

  // Handle dropdown interactions
  const handleDropdownHover = (dropdownId) => {
    if (window.innerWidth > 768) { // Only on desktop
      setActiveDropdown(dropdownId);
    }
  };

  const handleDropdownClick = (dropdownId) => {
    if (window.innerWidth <= 768) { // Only on mobile
      setActiveDropdown(activeDropdown === dropdownId ? null : dropdownId);
    }
  };

  const handleDropdownLeave = () => {
    if (window.innerWidth > 768) {
      setActiveDropdown(null);
    }
  };

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (headerRef.current && !headerRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Check if nav item should be shown based on permissions
  const shouldShowNavItem = (item) => {
    if (!item.requiresPermission) return true;
    return permissions[item.requiresPermission];
  };

  // Show minimal header (logo only) for login page or unauthenticated users
  if (isLoginPage || !isAuthenticated()) {
    return (
      <header className="header">
        <div className="header-container">
          <div className="logo-title-wrapper">
            <div className="logo-title-container">
              <img src={logo} alt="Cafua Management Company Logo" className="logo" />
              <h1 className="header-title">
                <span className="text-pink">People Plan </span>
                <span className="text-orange">App</span>
              </h1>
            </div>
          </div>
        </div>
      </header>
    );
  }

  return (
    <header className="header" ref={headerRef}>
      <div className="header-container">
        <div className="logo-title-wrapper">
          <div className="logo-title-container">
            <img src={logo} alt="Cafua Management Company Logo" className="logo" />
            <h1 className="header-title">
              <span className="text-pink">People Plan </span>
              <span className="text-orange">App</span>
            </h1>
          </div>
        </div>

        {/* Desktop Navigation */}
        <div className="nav-wrapper desktop-nav">
          <nav className="nav-buttons">
            {canSeeEnvSwitch() && (
              <a
                href={getEnvSwitchUrl()}
                className="nav-button"
                style={{ textDecoration: 'none' }}
              >
                {getEnvSwitchLabel()}
              </a>
            )}
            {navigationConfig.map((item) => {
              if (!shouldShowNavItem(item)) return null;

              if (item.type === 'dropdown') {
                return (
                  <NavDropdown
                    key={item.id}
                    item={item}
                    isActive={activeDropdown === item.id}
                    onHover={() => handleDropdownHover(item.id)}
                    onClick={() => handleDropdownClick(item.id)}
                    onLeave={handleDropdownLeave}
                    currentPath={location.pathname}
                    permissions={permissions}
                  />
                );
              }

              return (
                <NavItem
                  key={item.id}
                  item={item}
                  currentPath={location.pathname}
                />
              );
            })}
          </nav>
          <button className="logout-button" onClick={handleLogout}>
            Log Out
          </button>
        </div>

        {/* Mobile Menu Toggle */}
        <button
          className="mobile-menu-toggle"
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          aria-label="Toggle navigation menu"
        >
          <span className={`hamburger ${mobileMenuOpen ? 'open' : ''}`}>
            <span></span>
            <span></span>
            <span></span>
          </span>
        </button>
      </div>

      {/* Mobile Navigation */}
      <MobileMenu
        isOpen={mobileMenuOpen}
        navigationConfig={navigationConfig}
        permissions={permissions}
        currentPath={location.pathname}
        onClose={() => setMobileMenuOpen(false)}
        onLogout={handleLogout}
        showEnvSwitch={canSeeEnvSwitch()}
        envSwitchUrl={getEnvSwitchUrl()}
        envSwitchLabel={getEnvSwitchLabel()}
      />
    </header>
  );
}

export default Header;